// @owners { team: patients }
import {
  ActionSheetContext,
  ActionSheetV2,
  Button,
  InputRadio,
  InputRadioGroup,
  LgPadding,
  ListDescription,
} from '@alto/design-system';
import { Sentry } from '@alto/performance';
import { type MultipleActiveRxPrescription } from '@alto/scriptdash/alto/patient_app/checkout_flow/v1/checkout_flow_endpoint';
import React, { useContext, useState } from 'react';
import RX from '~shared/assets/images/RX.png';
import { getDoctorName } from '~shared/features/my-meds/helper';
import { useNextAvailableDatesForPrescriptions } from '~shared/features/next-available-date/queries/useNextAvailableDatesForPrescriptions';
import {
  type AvailabilitySortData,
  useAvailabilityDataByPrescriptionID,
} from '~shared/features/prescriptions/hooks/useAvailabilityDataByPrescriptionID';
import { formatRelativeDate, formatRelativeDateWithDow } from '~shared/helpers/date';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { NextAvailableDateActionSheet } from '../../next-available-date/components/next-available-date-action-sheet/NextAvailableDateActionSheet';

type Props = {
  readonly hasSheetsLeft: boolean;
  readonly onPrescriptionSelect: (prescription: MultipleActiveRxPrescription) => void;
  readonly prescriptions: MultipleActiveRxPrescription[];
};

/**
 * Action sheet for selecting a prescription to add to the cart when there are multiple prescriptions for the same medication.
 */
export const SelectPrescriptionActionSheet = ({ hasSheetsLeft, onPrescriptionSelect, prescriptions }: Props) => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const [selectedPrescription, setSelectedPrescription] = useState<MultipleActiveRxPrescription>();
  const availabilityData = useAvailabilityDataByPrescriptionID();
  const { nextAvailableDatesByPrescriptionID } = useNextAvailableDatesForPrescriptions();

  const handleSelectPrescription = (prescriptionID: string) => {
    const prescription = prescriptions.find((p) => p.id === parseInt(prescriptionID, 10));
    setSelectedPrescription(prescription);
  };

  const openNextAvailableDateActionSheet = (prescriptionID: number) => {
    const availabilityForPrescription: AvailabilitySortData | undefined = availabilityData[prescriptionID];
    const earliestAvailableDate = availabilityForPrescription?.earliestAvailableDate ?? null;
    const earliestDateReason = nextAvailableDatesByPrescriptionID[prescriptionID]?.earliest?.reason || '';
    setActiveActionSheet(
      <NextAvailableDateActionSheet
        earliestAvailableDate={earliestAvailableDate}
        earliestDateReason={earliestDateReason}
        prescriptionID={prescriptionID}
        orderTypeContext="Add to cart"
        analyticsOrigin={ORIGIN_NAMES.MULTIPLE_ACTIVE_RX_SAME_AVAILABILITY_DATE}
      />,
    );
  };

  return (
    <ActionSheetV2
      illustrationSrc={RX}
      title="Select which prescription to add"
      description="You have multiple prescriptions for this medication"
      analyticsName="multiple active rx"
    >
      <InputRadioGroup onValueChange={handleSelectPrescription}>
        {prescriptions?.map((prescription) => {
          const earliestAvailableDate = availabilityData[prescription.id].earliestAvailableDate;

          if (!earliestAvailableDate) {
            Sentry.captureMessage('cannot locate earliestAvailableDate', {
              level: 'info',
              extra: {
                availabilityData,
                prescriptionId: prescription.id,
                error: 'https://alto-pharmacy.sentry.io/issues/4609709056',
              },
            });
          }

          return (
            <InputRadio
              key={prescription.id}
              label={`Prescribed ${formatRelativeDate(prescription.date_written ?? '', { includeRelative: true })}`}
              value={prescription.id.toString()}
              descriptions={[
                <ListDescription
                  key={`${prescription.id}-doctor-name`}
                  iconName="doctor-small"
                  type="grey"
                >{`by ${getDoctorName(prescription.doctor)}`}</ListDescription>,
                <ListDescription
                  key={`${prescription.id}-refills-remaining`}
                  iconName="pillbottle-small"
                  type="grey"
                >
                  {`${prescription.refills_remaining ?? 'no'} refills remaining`}
                </ListDescription>,
                <ListDescription
                  key={`${prescription.id}-next-available-date`}
                  iconName="calendar-small"
                  type="secondary"
                  onPress={() => {
                    openNextAvailableDateActionSheet(prescription.id);
                  }}
                >{`Available ${formatRelativeDateWithDow(earliestAvailableDate || '')}`}</ListDescription>,
              ]}
            />
          );
        })}
      </InputRadioGroup>
      <LgPadding>
        <Button
          disabled={!selectedPrescription}
          label={hasSheetsLeft ? 'Select prescription' : 'Add to cart'}
          onPress={() => {
            if (selectedPrescription) {
              onPrescriptionSelect(selectedPrescription);
            }
          }}
        />
      </LgPadding>
    </ActionSheetV2>
  );
};
